import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { CreatorOrderEarnings } from '@/interface/creatorOrderEarnings';
import type { CreatorDeliveryStage } from '@/interface/creatorOrderV2';
import axiosInstance from '@/apis/axiosInstance';
import { assertCreatorOrderV2Success } from '@/apis/creatorOrderV2/errors';
import { assertCreatorDeliveryTimeline } from '@/apis/creatorOrderV2/deliveryTimeline';
import type {
  CreatorAccountInfoRequest,
  CreatorAddressRequest,
  CreatorAiReviewCommandData,
  CreatorAiReviewRequest,
  CreatorAiReviewResultData,
  CreatorDraftRequest,
  CreatorFinalLinkAiReviewResponse,
  CreatorFinalLinkAiReviewStartResponse,
  CreatorFinalLinkRequest,
  CreatorFinalLinkResponse,
  CreatorJoinRequest,
  CreatorJoinResponse,
  CreatorNegotiateRequest,
  CreatorOrderDetailV2,
  CreatorAudienceViewV2,
  CreatorOrderAudienceV2,
  CreatorOrderListByTabRequest,
  CreatorOrderRecordV2,
  CreatorOrderStatsByTabRequest,
  CreatorOrderV2Envelope,
  CreatorOrderV2Result,
  CreatorPackagePriceRecommendationRequest,
  CreatorPackagePriceRecommendationResponse,
  CreatorQuotePreviewRequest,
  CreatorQuotePreviewResponse,
  CreatorProductInfoRequest,
  CreatorRejectReason
} from '@/interface/creatorOrderV2';

const encodeOrderId = (orderId: string) => encodeURIComponent(orderId);

export type CreatorAppealRequest = {
  reason: string;
  files?: string[];
} & (
  | { stage: CreatorDeliveryStage; version: number }
  | { stage?: never; version?: never }
);

const appendHash = (path: string, hash?: string | null) =>
  hash ? `${path}?hash=${encodeURIComponent(hash)}` : path;

const skipAuthConfig = { skipAuth: true } as AxiosRequestConfig;

const unwrapResponse = <T>(
  response: AxiosResponse<CreatorOrderV2Envelope<T>>
): CreatorOrderV2Result<T> => assertCreatorOrderV2Success<T>(response.data);

const get = async <T>(
  path: string,
  config?: AxiosRequestConfig
): Promise<CreatorOrderV2Result<T>> =>
  unwrapResponse(
    await axiosInstance.get<CreatorOrderV2Envelope<T>>(path, config)
  );

const post = async <T>(
  path: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<CreatorOrderV2Result<T>> =>
  unwrapResponse(
    await axiosInstance.post<CreatorOrderV2Envelope<T>>(path, body, config)
  );

const put = async <T>(
  path: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<CreatorOrderV2Result<T>> =>
  unwrapResponse(
    await axiosInstance.put<CreatorOrderV2Envelope<T>>(path, body, config)
  );

export const creatorOrderV2Api = {
  applyPostpone: (orderId: string, body: { days: number; note?: string }) =>
    post<Record<string, never>>(
      `/v2/order/${encodeOrderId(orderId)}/applyPostpone`,
      body
    ),

  appealOverdue: (orderId: string, body: CreatorAppealRequest) =>
    post<{ appealId: string }>(
      `/v2/order/${encodeOrderId(orderId)}/appealOverdue`,
      body
    ),

  listByTab: <T = CreatorOrderRecordV2[]>(
    body: CreatorOrderListByTabRequest,
    options: { hash?: string | null; open?: boolean } = {}
  ) =>
    post<T>(
      appendHash(
        options.open
          ? '/influencer/v2/order/open/listByTab'
          : '/influencer/v2/order/listByTab',
        options.hash
      ),
      body,
      options.open ? skipAuthConfig : undefined
    ),

  statsByTab: <T = CreatorOrderRecordV2[]>(
    body: CreatorOrderStatsByTabRequest = {},
    options: { hash?: string | null; open?: boolean } = {}
  ) =>
    post<T>(
      appendHash(
        options.open
          ? '/influencer/v2/order/open/statsByTab'
          : '/influencer/v2/order/statsByTab',
        options.hash
      ),
      body,
      options.open ? skipAuthConfig : undefined
    ),

  dashboard: <T = CreatorOrderRecordV2>(hash?: string | null) =>
    get<T>(appendHash('/influencer/v2/order/dashboard', hash)),

  stats: <T = CreatorOrderRecordV2>(hash?: string | null) =>
    get<T>(appendHash('/influencer/v2/order/stats', hash)),

  detail: (orderId: string, validate?: boolean) =>
    get<CreatorOrderDetailV2>(
      `/v2/order/detail/${encodeOrderId(orderId)}${validate ? '?validate=true' : ''}`
    ),

  audience: (orderId: string) =>
    get<CreatorOrderAudienceV2>(`/v2/order/${encodeOrderId(orderId)}/audience`),

  triggerAudienceSelfAnalysis: (channelKey: string) =>
    post<CreatorAudienceViewV2>('/influencer/v2/audience/self-analysis', {
      channelKey
    }),

  deliveryTimeline: async (orderId: string) => {
    const response = await get<unknown>(
      `/v2/order/${encodeOrderId(orderId)}/deliveryTimeline`
    );
    return {
      ...response,
      data: assertCreatorDeliveryTimeline(response.data, orderId)
    };
  },

  earningDetails: <T = CreatorOrderEarnings>(orderId: string) =>
    get<T>(`/v2/order/${encodeOrderId(orderId)}/earningDetails`),

  packagePriceRecommendation: (
    orderId: string,
    body: CreatorPackagePriceRecommendationRequest
  ) =>
    post<CreatorPackagePriceRecommendationResponse>(
      `/v2/order/${encodeOrderId(orderId)}/packagePriceRecommendation`,
      body
    ),

  quotePreview: (orderId: string, body: CreatorQuotePreviewRequest) =>
    post<CreatorQuotePreviewResponse>(
      `/v2/order/${encodeOrderId(orderId)}/quotePreview`,
      body
    ),

  join: <T = CreatorJoinResponse>(orderId: string, body: CreatorJoinRequest) =>
    post<T>(`/v2/order/${encodeOrderId(orderId)}/join`, body),

  negotiate: <T = CreatorOrderRecordV2>(
    orderId: string,
    body: CreatorNegotiateRequest
  ) => post<T>(`/v2/order/${encodeOrderId(orderId)}/negotiate`, body),

  creatorReject: <T = CreatorOrderRecordV2>(
    orderId: string,
    reasons: CreatorRejectReason[]
  ) => post<T>(`/v2/order/${encodeOrderId(orderId)}/creatorReject`, reasons),

  address: <T = CreatorOrderRecordV2>(
    orderId: string,
    body: CreatorAddressRequest,
    idempotencyKey?: string
  ) =>
    post<T>(
      `/v2/order/${encodeOrderId(orderId)}/address`,
      body,
      idempotencyKey
        ? { headers: { 'Idempotency-Key': idempotencyKey } }
        : undefined
    ),

  productInfo: <T = CreatorOrderRecordV2>(
    orderId: string,
    body: CreatorProductInfoRequest
  ) => post<T>(`/v2/order/${encodeOrderId(orderId)}/productInfo`, body),

  accountInfo: <T = CreatorOrderRecordV2>(
    orderId: string,
    body: CreatorAccountInfoRequest,
    idempotencyKey?: string
  ) =>
    post<T>(
      `/v2/order/${encodeOrderId(orderId)}/accountInfo`,
      body,
      idempotencyKey
        ? { headers: { 'Idempotency-Key': idempotencyKey } }
        : undefined
    ),

  kickoff: (orderId: string, idempotencyKey: string) =>
    post<CreatorOrderRecordV2>(
      `/v2/order/${encodeOrderId(orderId)}/kickoff`,
      {},
      {
        headers: { 'Idempotency-Key': idempotencyKey }
      }
    ),

  draft: <T = CreatorOrderRecordV2>(
    orderId: string,
    body: CreatorDraftRequest,
    idempotencyKey?: string
  ) =>
    post<T>(
      `/v2/order/${encodeOrderId(orderId)}/draft`,
      body,
      idempotencyKey
        ? { headers: { 'Idempotency-Key': idempotencyKey } }
        : undefined
    ),

  aiReview: (orderId: string, body: CreatorAiReviewRequest) =>
    post<CreatorAiReviewCommandData>(
      `/v2/order/${encodeOrderId(orderId)}/aiReview`,
      body
    ),

  getAiReview: (orderId: string, contentId: string) =>
    get<CreatorAiReviewResultData>(
      `/v2/order/${encodeOrderId(orderId)}/aiReview/${encodeURIComponent(contentId)}`
    ),

  finalLinkAiReview: (orderId: string, body: CreatorFinalLinkRequest) =>
    post<CreatorFinalLinkAiReviewStartResponse>(
      `/v2/order/${encodeOrderId(orderId)}/finalLinkAIReview`,
      body
    ),

  getFinalLinkAiReview: (orderId: string) =>
    get<CreatorFinalLinkAiReviewResponse>(
      `/v2/order/${encodeOrderId(orderId)}/finalLinkAIReview`
    ),

  promotionalLink: <T = CreatorFinalLinkResponse>(
    orderId: string,
    body: CreatorFinalLinkRequest,
    idempotencyKey?: string
  ) =>
    put<T>(
      `/v2/order/${encodeOrderId(orderId)}/promotionalLink`,
      body,
      idempotencyKey
        ? { headers: { 'Idempotency-Key': idempotencyKey } }
        : undefined
    )
};
